import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { SelectChangeEvent } from '@mui/material';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import {
    ALPHA_NUMBERIC,
    DECIMAL_NUMBER,
    FX30S_SERIAL,
    FX30_ETHERNET,
    IP_ADDRESS,
    ONLY_NUMBER,
    RESTRICT_DEVICE_WITHOUT_SPACE_REGEX,
    BLG300,
    BLG400,
    MESH,
} from '../../constants';
import { useDeviceMaster } from '../../data/DataProviders/DeviceMasterProvider';
import { isDataLoaded } from '../../types';
import CircularProgress from '@mui/material/CircularProgress';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { DeviceAutoComplete } from '../DeviceAutoComplete/DeviceAutoComplete';

import { GatewayDeviceRegistration, ChildDevice, GatewayDevice, uart } from '../../types/device';

import { useDispatch, useSelector } from 'react-redux';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { useAuthHelper } from '../AuthContextProvider';
import { Thunks } from '../../actions';
import { SierraDeviceSelectors } from '../../selectors/device-selectors';
//import MaskedInput from 'react-text-mask';
import { EmptyState } from '@brightlayer-ui/react-components/core';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { DataIndicators } from '../DataIndicators';
import { NavigationPromptModal } from '../NavigationPromptModal/NavigationPromptModal';
import { SierraDeviceActions } from '../../actions/actions/sierra-device-actions';
import { baudData, bits, parity, standard, stop, term } from '../../constants/device-data';
import { AddPXDBDevice } from '../AddPXDBDevice/AddPXDBDevice';
import { AddMeshDevice } from '../AddMeshDevice/AddMeshDevice';
import { AddStarfishDevice } from '../AddStarFish/AddStarfish';
import { useGetOrganizationCheckDetails } from '../../data/DataProviders/GetOrgCheckProvider';

export const AddDevice: React.FC<{
    onCancel: Function;
    siteId: string;
    organizationName: string;
    deviceTypeList: Array<{
        key: string;
        value: string;
        gatewayProfileId: string;
        netmask: number;
        uart: uart;
        forWeb: boolean;
        vendor?: string;
    }>;
    device: string;
    pxdbDeviceTypeList: Array<{
        type: string;
    }>;
}> = (props) => {
    const { t } = useLanguageLocale();
    const classes = deviceStyles();
    const deviceMasterData = useDeviceMaster();
    const dispatch = useDispatch();
    const { addToast } = useToasts();
    const authHelper = useAuthHelper();
    const gatewayDeviceState = useSelector(SierraDeviceSelectors.deviceRegistration);
    const [showGatewayType, setShowGatewayType] = React.useState(false);
    const [deviceTypeValue, setDeviceTypeValue] = React.useState('');
    const [selectedDevice, setSelectedDevice] = React.useState<GatewayDevice>({
        childDevices: [],
        blueprint: {
            id: '',
            version: '',
        },
        childDeviceTypes: '',
        deviceProfileId: '',
    });
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [childDeviceList, setChildDeviceList] = React.useState<ChildDevice[]>();
    const [gatewayDevice, setGatewayDevice] = useState<GatewayDeviceRegistration>();
    const [blueprintErr, setBlueprintErr] = React.useState(false);
    // const ALPHA_NUMERIC_DASH_REGEX = /^[a-z0-9\b]+$/;
    const [webGateway, setwebGateway] = React.useState(props.deviceTypeList);
    const [commonGateway, setCommonGateway] = React.useState(props.deviceTypeList);
    const [notMeshGateway, setNotMeshGateway] = React.useState(props.deviceTypeList);
    const getOrgCheckDetails = useGetOrganizationCheckDetails();
    const [getOrgTypeData, setgetOrgTypeData] = useState<any | null>(null);
    const [hasPXDashboard, setHasPXDashboard] = React.useState(false);

    useEffect(() => {
        if (isDataLoaded(deviceMasterData.deviceTypeData)) {
            const result = props.deviceTypeList.filter((item) => Object.prototype.hasOwnProperty.call(item, 'forWeb'));
            const response = result.filter((item) => item.forWeb === true);

            setwebGateway(response);
        }
    }, [props.deviceTypeList]);
    useEffect(() => {
        if (isDataLoaded(deviceMasterData.deviceTypeData)) {
            const filterCommonGateway = props.deviceTypeList.filter(
                (item) => Object.prototype.hasOwnProperty.call(item, 'forWeb') === false
            );
            setCommonGateway(filterCommonGateway);
        }
    }, [props.deviceTypeList]);
    const combineGatewayForWeb = [...webGateway, ...commonGateway];
    useEffect(() => {
        if (isDataLoaded(deviceMasterData.deviceTypeData)) {
            const filterCommonGateway = props.deviceTypeList.filter((item) => item.key !== 'Mesh');
            setNotMeshGateway(filterCommonGateway);
        }
    }, [props.deviceTypeList]);

    useEffect(() => {
        if (isDataLoaded(deviceMasterData.deviceInfo) && deviceMasterData.rowKey === 'childdevices') {
            setChildDeviceList(deviceMasterData.deviceInfo.value);
        }
    }, [deviceMasterData]);
    useEffect(() => {
        if (isDataLoaded(getOrgCheckDetails)) {
            setgetOrgTypeData(getOrgCheckDetails.value);
        }
    }, [getOrgCheckDetails]);
    useEffect(() => {
        dispatch(SierraDeviceActions.sierraGatewayDeviceMasterData(true));
        if (props.siteId !== '' && isDataLoaded(deviceMasterData.deviceTypeData)) {
            const device: GatewayDeviceRegistration = {
                siteId: props.siteId,
                name: '',
                modelType: '',
                deviceProfileId: '',
                serial: '',
                modbusAddress: '',
                imei: '',
                childDeviceTypes: '',
                blueprint: {
                    id: '',
                    version: '',
                },
                validateInput: true,
                uart: {
                    baud: '',
                    bits: 5,
                    flow: '',
                    pair: '',
                    stop: 0,
                    std: '',
                    term: '',
                },
                netmask: 0,
            };
            setGatewayDevice(device);
        }
    }, [props.siteId, deviceMasterData.deviceTypeData]); //eslint-disable-line react-hooks/exhaustive-deps

    const isDirty =
        deviceTypeValue !== '' ||
        (selectedDevice && selectedDevice.childDevices !== undefined && selectedDevice.childDevices.length > 0) ||
        (gatewayDevice &&
            (gatewayDevice.serial !== '' ||
                gatewayDevice.name !== '' ||
                gatewayDevice.imei !== '' ||
                gatewayDevice.modbusAddress !== '')) ||
        false;

    const isDisableEthernet =
        gatewayDevice &&
        selectedDevice.childDevices !== undefined &&
        selectedDevice.childDevices.length > 0 &&
        gatewayDevice.modbusAddress.trim() &&
        gatewayDevice.name.trim() &&
        gatewayDevice.serial.trim() &&
        gatewayDevice.imei.trim() &&
        gatewayDevice.netmask > 0 &&
        !blueprintErr;

    const isDisableSerial =
        gatewayDevice &&
        selectedDevice &&
        selectedDevice.childDevices !== undefined &&
        selectedDevice.childDevices.length > 0 &&
        gatewayDevice.name !== '' &&
        gatewayDevice.serial !== '' &&
        gatewayDevice.imei !== '' &&
        !blueprintErr;

    const isDisable = (): boolean | undefined => {
        if (!gatewayDeviceState.gatewayLoading)
            return deviceTypeValue.toLowerCase() === FX30_ETHERNET
                ? Boolean(isDisableEthernet)
                : deviceTypeValue.toLowerCase() === FX30S_SERIAL
                ? isDisableSerial
                : true;
        return true;
    };

    const handleGatewayType = (e: SelectChangeEvent): void => {
        setDeviceTypeValue(e.target.value);
        setShowGatewayType(true);
        if (e.target.value.toLowerCase() === FX30S_SERIAL || e.target.value.toLowerCase() === FX30_ETHERNET) {
            deviceMasterData.getDevicesInfo(props.organizationName, 'childdevices');
            const gatewayObj = props.deviceTypeList.find((x) => x.key.toLowerCase() === e.target.value.toLowerCase());
            if (gatewayDevice) {
                const deviceModelObj: GatewayDeviceRegistration = { ...gatewayDevice };
                deviceModelObj.modelType = e.target.value;
                if (gatewayObj && gatewayObj.uart) {
                    deviceModelObj.netmask = gatewayObj.netmask;
                    deviceModelObj.uart.flow = gatewayObj.uart.flow;
                    deviceModelObj.uart.baud = gatewayObj.uart.baud;
                    deviceModelObj.uart.bits = gatewayObj.uart.bits;
                    deviceModelObj.uart.pair = gatewayObj.uart.pair;
                    deviceModelObj.uart.std = gatewayObj.uart.std;
                    deviceModelObj.uart.stop = gatewayObj.uart.stop;
                    deviceModelObj.uart.term = gatewayObj.uart.term;
                    deviceModelObj.deviceProfileId = gatewayObj.gatewayProfileId;
                }
                setGatewayDevice(deviceModelObj);
            }
        }
    };
    useEffect(() => {
        const result = (props.pxdbDeviceTypeList ?? []).some((x) => x.type === deviceTypeValue);
        setHasPXDashboard(result);
    }, [deviceTypeValue]);

    const selectedDevices = (device: GatewayDevice): void => {
        setSelectedDevice(device);
    };

    const blueprintError = (bError: boolean): void => {
        setBlueprintErr(bError);
    };

    const onDevicesCancel = (): void => {
        props.onCancel();
    };
    const onChangeValues = (value: string | number, type: string): void => {
        if (gatewayDevice) {
            const deviceObj: any = { ...gatewayDevice };
            deviceObj[`${type}`] = value;
            setGatewayDevice(deviceObj);
        }
    };
    const onChangeValuesOfUart = (value: string | number, type: string): void => {
        if (gatewayDevice) {
            const deviceObj: any = { ...gatewayDevice };
            deviceObj.uart[`${type}`] = value;
            setGatewayDevice(deviceObj);
        }
    };
    const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (RESTRICT_DEVICE_WITHOUT_SPACE_REGEX.test(e.target.value)) onChangeValues(e.target.value, 'name');
    };

    const onChangeAddress = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || DECIMAL_NUMBER.test(e.target.value)) {
            setIPAddressError(false);
            onChangeValues(e.target.value, 'modbusAddress');
        }
    };
    const onChangeIMEI = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            onChangeValues(e.target.value, 'imei');
        }
    };
    const onChangeSerialNo = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) {
            onChangeValues(e.target.value, 'serial');
        }
    };
    const onChangeNetmask = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        if (e.target.value === '' || ONLY_NUMBER.test(e.target.value)) {
            onChangeValues(e.target.value === '' ? '' : Number(e.target.value), 'netmask');
        }
    };

    const onChangeFlow = (e: React.ChangeEvent<{ name?: string | undefined; value: unknown }>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value as string)) {
            onChangeValuesOfUart(e.target.value as string, 'flow');
        }
    };
    //console.log('setDeviceTypeValue', deviceTypeValue);

    const onSubmit = (): void => {
        let checkIPAddress = true;
        if (gatewayDevice) {
            if (
                gatewayDevice.modelType.toLowerCase() === FX30_ETHERNET &&
                !IP_ADDRESS.test(gatewayDevice.modbusAddress)
            ) {
                setIPAddressError(true);
                checkIPAddress = false;
            } else {
                setIPAddressError(false);
                checkIPAddress = true;
            }
            if (checkIPAddress) {
                const deviceValue = selectedDevice.childDevices && selectedDevice.childDevices.map((x) => x.value);
                const gatewayDeviceData = {
                    ...gatewayDevice,
                    blueprint: {
                        id: selectedDevice.blueprint.id || '',
                        version: selectedDevice.blueprint.version || '',
                    },
                    childDeviceTypes: selectedDevice.childDeviceTypes,
                };
                dispatch(
                    Thunks.SierraDevice.GatewayDeviceRegistration(gatewayDeviceData, deviceValue, authHelper, addToast)
                );
            }
        }
    };

    return (
        <Box>
            {props.deviceTypeList && props.deviceTypeList.length > 0 && (
                <>
                    <Box className={classes.padding8}>
                        <FormControl variant="filled" required className={classes.fullWidth}>
                            <InputLabel id="deviceTypeData" data-testid="device-type">
                                Connected Device Type
                            </InputLabel>
                            <Select
                                label="deviceType"
                                onChange={(e): void => {
                                    handleGatewayType(e);
                                }}
                                value={deviceTypeValue}
                                required
                                inputProps={{
                                    'data-testid': 'device-type-dd',
                                }}
                            >
                                {isDataLoaded(deviceMasterData.deviceTypeData) &&
                                (getOrgTypeData === null || !getOrgTypeData.isChamp)
                                    ? notMeshGateway.map(
                                          (deviceData: { key: string; value: string }, index: number) => (
                                              <MenuItem
                                                  key={index}
                                                  value={deviceData.key}
                                                  data-testid={`device-type-menu${index}`}
                                              >
                                                  {deviceData.value}
                                              </MenuItem>
                                          )
                                      )
                                    : combineGatewayForWeb.map(
                                          (deviceData: { key: string; value: string }, index: number) => (
                                              <MenuItem
                                                  key={index}
                                                  value={deviceData.key}
                                                  data-testid={`device-type-menu${index}`}
                                              >
                                                  {deviceData.value}
                                              </MenuItem>
                                          )
                                      )}
                            </Select>
                        </FormControl>
                    </Box>
                    {!isDataLoaded(deviceMasterData.deviceInfo) && deviceMasterData.rowKey === 'childdevices' && (
                        <DataIndicators
                            errorMessage="Record not found"
                            emptyMessage="Record not found"
                            inline
                            data={deviceMasterData.deviceInfo}
                            emptyIcon="Devices"
                            isEmpty={false}
                            isDetailedInfo={false}
                        />
                    )}
                    {hasPXDashboard && (
                        <AddPXDBDevice
                            onCancel={props.onCancel}
                            siteId={props.siteId}
                            deviceTypeList={props.deviceTypeList}
                            organizationName={props.organizationName}
                            selectedDevice={deviceTypeValue}
                            device={'gateway'}
                        ></AddPXDBDevice>
                    )}
                    {deviceTypeValue.toLowerCase() === MESH ? (
                        <AddMeshDevice
                            onCancel={props.onCancel}
                            siteId={props.siteId}
                            deviceTypeList={props.deviceTypeList}
                            organizationName={props.organizationName}
                            device={'gateway'}
                        ></AddMeshDevice>
                    ) : (
                        ''
                    )}
                    {(deviceTypeValue.toLowerCase() === BLG300 || deviceTypeValue.toLowerCase() === BLG400) && (
                        <AddStarfishDevice
                            onCancel={props.onCancel}
                            siteId={props.siteId}
                            deviceTypeList={props.deviceTypeList}
                            organizationName={props.organizationName}
                            device={'gateway'}
                            deviceTypeValue={deviceTypeValue}
                        ></AddStarfishDevice>
                    )}
                    {showGatewayType &&
                    childDeviceList &&
                    childDeviceList.length > 0 &&
                    gatewayDevice &&
                    !hasPXDashboard &&
                    deviceTypeValue.toLowerCase() !== MESH &&
                    deviceTypeValue.toLowerCase() !== BLG300 &&
                    deviceTypeValue.toLowerCase() !== BLG400 ? (
                        <>
                            <Box className={classes.padding8}>
                                <TextField
                                    inputProps={{ 'data-testid': 'device-name' }}
                                    className={classes.fullWidth}
                                    required
                                    variant="filled"
                                    value={gatewayDevice.name}
                                    id="DeviceName"
                                    label="Connected Device Name"
                                    onChange={(e): void => onChangeName(e)}
                                />
                            </Box>
                            <Box className={classes.padding8}>
                                <DeviceAutoComplete
                                    childDeviceList={childDeviceList}
                                    selectedDevices={selectedDevices}
                                    isBlueprintExist={blueprintErr}
                                    orgName={props.organizationName}
                                    blueprintError={blueprintError}
                                />
                            </Box>

                            <Box className={classes.padding8}>
                                <TextField
                                    inputProps={{ 'data-testid': 'serial', maxLength: 15 }}
                                    className={classes.fullWidth}
                                    required
                                    id="SerialNo"
                                    variant="filled"
                                    error={gatewayDeviceState.errorCode === '9021'}
                                    helperText={
                                        gatewayDeviceState.errorCode === '9021'
                                            ? 'This is not a valid Serial Number and IMEI combination.'
                                            : ''
                                    }
                                    value={gatewayDevice.serial}
                                    label="Serial Number"
                                    onChange={(e): void => {
                                        onChangeSerialNo(e);
                                    }}
                                />
                            </Box>
                            <Box className={classes.padding8}>
                                <TextField
                                    inputProps={{ 'data-testid': 'IMEI', maxLength: 15 }}
                                    className={classes.fullWidth}
                                    required
                                    value={gatewayDevice.imei}
                                    id="IMEI"
                                    variant="filled"
                                    label="IMEI"
                                    error={gatewayDeviceState.errorCode === '9021'}
                                    helperText={
                                        gatewayDeviceState.errorCode === '9021'
                                            ? 'This is not a valid Serial Number and IMEI combination'
                                            : ''
                                    }
                                    onChange={(e): void => {
                                        onChangeIMEI(e);
                                    }}
                                />
                            </Box>

                            {deviceTypeValue.toLowerCase() === FX30_ETHERNET && (
                                <>
                                    <Box className={classes.padding8}>
                                        <TextField
                                            label="Modbus (TCP/IP) Address"
                                            required
                                            variant="filled"
                                            className={classes.fullWidth}
                                            value={gatewayDevice.modbusAddress}
                                            onChange={(e): void => {
                                                onChangeAddress(e);
                                            }}
                                            error={ipAddressError}
                                            helperText={ipAddressError ? 'Invalid IP Address' : ''}
                                            name="numberformat"
                                            id="ModbusAddress"
                                            inputProps={{ 'data-testid': 'ip-address' }}
                                        />
                                    </Box>
                                    <Box className={classes.padding8}>
                                        <TextField
                                            label="Netmask"
                                            required
                                            variant="filled"
                                            className={classes.fullWidth}
                                            value={gatewayDevice.netmask}
                                            onChange={(e): void => {
                                                onChangeNetmask(e);
                                            }}
                                            name="numberformat"
                                            id="netmask"
                                            inputProps={{ 'data-testid': 'netmask' }}
                                        />
                                    </Box>
                                </>
                            )}
                            {deviceTypeValue.toLowerCase() === FX30S_SERIAL && (
                                <>
                                    <Box className={classes.padding8}>
                                        <FormControl variant="filled" required className={classes.fullWidth}>
                                            <InputLabel id="baudRateData" data-testid="baud-rate">
                                                Baud Rate
                                            </InputLabel>
                                            <Select
                                                label="baudRate"
                                                value={gatewayDevice.uart.baud}
                                                required
                                                inputProps={{ 'data-testid': 'baud-rate-dd' }}
                                                onChange={(e): void => {
                                                    onChangeValuesOfUart(e.target.value, 'baud');
                                                }}
                                            >
                                                {baudData.map((data: { key: string; value: string }, index: number) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={data.key}
                                                        data-testid={`baud-data${index}`}
                                                    >
                                                        {data.value}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="bits" data-testid="bits">
                                                    Data Bits
                                                </InputLabel>
                                                <Select
                                                    label="bits"
                                                    value={gatewayDevice.uart.bits}
                                                    required
                                                    inputProps={{ 'data-testid': 'bits-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        onChangeValuesOfUart(e.target.value as string, 'bits');
                                                    }}
                                                >
                                                    {bits.map((data: { key: number; value: string }, index: number) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={data.key}
                                                            data-testid={`bits${index}`}
                                                        >
                                                            {data.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <TextField
                                                label="Flow"
                                                variant="filled"
                                                className={classes.fullWidth}
                                                value={'N'}
                                                onChange={(e): void => {
                                                    onChangeFlow(e);
                                                }}
                                                id="flow"
                                                inputProps={{ 'data-testid': 'flow', maxLength: 5 }}
                                                disabled
                                            />
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="parityData" data-testid="parity">
                                                    Parity
                                                </InputLabel>
                                                <Select
                                                    label="parity"
                                                    value={gatewayDevice.uart.pair}
                                                    required
                                                    inputProps={{ 'data-testid': 'parity-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        onChangeValuesOfUart(e.target.value, 'pair');
                                                    }}
                                                >
                                                    {parity.map(
                                                        (data: { key: string; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`parity${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="baudRateData" data-testid="baud-rate">
                                                    Stop Bits
                                                </InputLabel>
                                                <Select
                                                    label="baudRate"
                                                    value={gatewayDevice.uart.stop}
                                                    required
                                                    inputProps={{ 'data-testid': 'stop-bits-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        onChangeValuesOfUart(Number(e.target.value), 'stop');
                                                    }}
                                                >
                                                    {stop.map((data: { key: number; value: string }, index: number) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={data.key}
                                                            data-testid={`stop-bits${index}`}
                                                        >
                                                            {data.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.padding8}`}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="standardData" data-testid="standard">
                                                    Standard
                                                </InputLabel>
                                                <Select
                                                    label="standard"
                                                    value={gatewayDevice.uart.std}
                                                    required
                                                    inputProps={{ 'data-testid': 'standard-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        onChangeValuesOfUart(e.target.value, 'std');
                                                    }}
                                                >
                                                    {standard.map(
                                                        (data: { key: string; value: string }, index: number) => (
                                                            <MenuItem
                                                                key={index}
                                                                value={data.key}
                                                                data-testid={`standard${index}`}
                                                            >
                                                                {data.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box
                                            style={{ width: '50%', paddingRight: 0 }}
                                            className={`${classes.padding8}`}
                                        >
                                            <FormControl variant="filled" required fullWidth>
                                                <InputLabel id="termData" data-testid="term">
                                                    Terminating Resistor Support
                                                </InputLabel>
                                                <Select
                                                    label="term"
                                                    value={gatewayDevice.uart.term}
                                                    required
                                                    inputProps={{ 'data-testid': 'term-dd', width: '50%' }}
                                                    onChange={(e): void => {
                                                        onChangeValuesOfUart(e.target.value, 'term');
                                                    }}
                                                >
                                                    {term.map((data: { key: string; value: string }, index: number) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={data.key}
                                                            data-testid={`term${index}`}
                                                        >
                                                            {data.value}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </>
                            )}
                            <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '24px 8px' }}>
                                <Button
                                    style={{ marginRight: 16 }}
                                    variant="outlined"
                                    color="primary"
                                    data-testid="cancel-device"
                                    onClick={(): void => {
                                        onDevicesCancel();
                                    }}
                                >
                                    {t('ACTIONS.CANCEL')}
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={!isDisable()}
                                    startIcon={
                                        gatewayDeviceState.gatewayLoading ? (
                                            <CircularProgress size="1rem" style={{ color: 'white' }} />
                                        ) : (
                                            <></>
                                        )
                                    }
                                    color="primary"
                                    data-testid="add-device-button"
                                    onClick={() => {
                                        onSubmit();
                                    }}
                                >
                                    {t('ACTIONS.SUBMIT')}
                                </Button>
                            </Box>
                        </>
                    ) : (
                        ''
                    )}
                </>
            )}
            {!props.deviceTypeList && (
                <Box style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
                    <EmptyState
                        title="No record found under this organization"
                        icon={
                            <div style={{ fontSize: 48 }}>
                                <ReportProblemOutlinedIcon id={'fail-icon'} fontSize={'inherit'} />
                            </div>
                        }
                    />
                </Box>
            )}
            {!gatewayDeviceState.gatewaySuccess && <NavigationPromptModal isDirty={isDirty} />}
        </Box>
    );
};
